import {
  Box,
  Select,
  MenuItem,
  FormControl,
  Button,
  FormLabel,
  TextField,
  Stack,
  Tooltip,
  IconButton,
  Typography,
  Slider,
  Switch,
  Chip,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router';
import { InfoIcon, XIcon } from '@phosphor-icons/react';

import {
  getConfigValues,
  updateAssistantConfigValues,
  getAgentToolsApi,
  connectWhatsappVoiceLinkApi,
} from '../../../../utils/apis';
import {
  ConfigValuesResponse,
  IUpdateAssistantConfigValues,
  UltravoxToolData,
} from '../../../../schemas/apis/interface';
import { FC, useState, useEffect } from 'react';
import { useTimeZones } from '../../../../hooks/UseTimeZones';
import { useDashboard } from '../../../../hooks/useDashboard';
import MessagePopup from '../../../common/MessagePopup';
import { commonStyles } from '../../../../utils/styles/inputs';
import { useAlert } from '../../../../hooks/useAlert';
import { routes } from '../../../../constants/routes';
import theme from '../../../../theme';
import AgentWhatsappConfig from './AgentWhatsappConfig';
import PermissionGate from '../../../common/PermissionGate';

interface AgentConfigurationProps {
  isDrawer?: boolean;
  onClose?: () => void;
  alert?: ReturnType<typeof useAlert>['alert'];
  setAlert?: ReturnType<typeof useAlert>['setAlert'];
}

// Update utility function to handle seconds instead of milliseconds
const stripSecFromValue = (value: string | number): number => {
  if (typeof value === 'string') {
    const cleanValue = value.replace(/\s*s(ec)?\s*$/i, '').trim();
    const numValue = parseFloat(cleanValue);
    return isNaN(numValue) ? 0 : numValue;
  }
  return typeof value === 'number' ? value : 0;
};

const AgentConfiguration: FC<AgentConfigurationProps> = ({
  isDrawer,
  onClose,
  alert: externalAlert,
  setAlert: externalSetAlert,
}) => {
  const { data: configValues } = useQuery({
    queryKey: ['configValues'],
    queryFn: getConfigValues,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: 0,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 60,
  });

  // API call to fetch available tools
  const { data: toolsData, isLoading: isToolsLoading } = useQuery({
    queryKey: ['tools'],
    queryFn: getAgentToolsApi,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: false,
    staleTime: Infinity,
    gcTime: 1000 * 60 * 30,
  });

  // Get available tools from the API response
  const availableTools: UltravoxToolData[] =
    toolsData?.data?.data?.results || [];

  const { timeZones } = useTimeZones();
  const { selectedAssistant, selectedAssistantId } = useDashboard();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const configValuesData = configValues?.data
    ?.config[0] as ConfigValuesResponse;

  const selectedAssistantConfig = selectedAssistant?.config;

  const internalAlert = useAlert();
  const { alert, showAlert, setAlert } =
    isDrawer && externalAlert && externalSetAlert
      ? {
          alert: externalAlert,
          showAlert: (message: string, severity: 'error' | 'success') =>
            externalSetAlert(prev => ({
              ...prev,
              open: true,
              message,
              severity,
            })),
          setAlert: externalSetAlert,
        }
      : internalAlert;

  const [updateAssistantConfigValue, setUpdateAssistantConfigValues] =
    useState<IUpdateAssistantConfigValues | null>({
      agentId: selectedAssistantId!,
      llm: selectedAssistantConfig?.llm || 'OpenAI/gpt-4o',
      tts: selectedAssistantConfig?.tts || 'Deepgram',
      stt: selectedAssistantConfig?.stt || 'Deepgram',
      timeZone: selectedAssistantConfig?.timeZone || '',
      voice:
        selectedAssistantConfig?.voice?.name ||
        selectedAssistantConfig?.voice?.voiceName ||
        '',
      language: selectedAssistantConfig?.language?.name || '',
      latency: selectedAssistantConfig?.xiLabsConfig?.latency || 0.5,
      stability: selectedAssistantConfig?.xiLabsConfig?.stability || 0.5,
      speed: selectedAssistantConfig?.xiLabsConfig?.speed || 1.0,
      similarity: selectedAssistantConfig?.xiLabsConfig?.similarity || 0.75,
      responseDelay: selectedAssistantConfig?.responseDelay || 2.5,
      languageCapability: 'monolingual',
      noiseSuppression: selectedAssistantConfig?.noiseSuppression || false,
      warmTransfer: selectedAssistantConfig?.warmTransfer || false,
      userTimeout: stripSecFromValue(selectedAssistantConfig?.userTimeout || 0),
      variables: selectedAssistantConfig?.variables || [],
      vad: {
        turnEndpointDelay: stripSecFromValue(
          selectedAssistantConfig?.vad?.turnEndpointDelay || ''
        ),
        minimumTurnDuration: stripSecFromValue(
          selectedAssistantConfig?.vad?.minimumTurnDuration || ''
        ),
        minimumInterruptionDuration: stripSecFromValue(
          selectedAssistantConfig?.vad?.minimumInterruptionDuration || ''
        ),
        frameActivationThreshold:
          selectedAssistantConfig?.vad?.frameActivationThreshold || 0,
      },
      whatsappConfig: {
        model:
          selectedAssistantConfig?.whatsAppDetails?.model || 'OpenAI/gpt-4o',
        historyResponse:
          selectedAssistantConfig?.whatsAppDetails?.historyResponse || 10,
        debugMode: selectedAssistantConfig?.whatsAppDetails?.debugMode || false,
        whatsappPhonenumberId:
          selectedAssistantConfig?.whatsAppDetails?.whatsappPhonenumberId || '',
        whatsappToken:
          selectedAssistantConfig?.whatsAppDetails?.whatsappToken || '',
        whatsappVerificationToken:
          selectedAssistantConfig?.whatsAppDetails?.whatsappVerificationToken ||
          '',
      },
      tools: selectedAssistantConfig?.tools || [],
    });

  useEffect(() => {
    if (selectedAssistant?.config) {
      setUpdateAssistantConfigValues({
        agentId: selectedAssistantId!,
        llm: selectedAssistant.config.llm || 'OpenAI/gpt-4o',
        tts: selectedAssistant.config.tts || 'Deepgram',
        stt: selectedAssistant.config.stt || 'Deepgram',
        timeZone: selectedAssistant.config.timeZone || '',
        voice:
          selectedAssistant.config.voice?.name ||
          selectedAssistant.config.voice?.voiceName ||
          '',
        language: selectedAssistant.config.language?.name || '',
        latency: selectedAssistant.config.xiLabsConfig?.latency || 0.5,
        stability: selectedAssistant.config.xiLabsConfig?.stability || 0.5,
        speed: selectedAssistant.config.xiLabsConfig?.speed || 1.0,
        similarity: selectedAssistant.config.xiLabsConfig?.similarity || 0.75,
        responseDelay: selectedAssistant.config.responseDelay || 2.5,
        languageCapability: 'monolingual',
        noiseSuppression: selectedAssistant.config.noiseSuppression || false,
        warmTransfer: selectedAssistant.config.warmTransfer || false,
        userTimeout: stripSecFromValue(
          selectedAssistant.config.userTimeout || 0
        ),
        variables: selectedAssistant.config.variables || [],
        whatsappConfig: {
          model:
            selectedAssistant.config.whatsAppDetails?.model || 'OpenAI/gpt-4o',
          historyResponse:
            selectedAssistant.config.whatsAppDetails?.historyResponse || 10,
          debugMode:
            selectedAssistant.config.whatsAppDetails?.debugMode || false,
          whatsappPhonenumberId:
            selectedAssistant.config.whatsAppDetails?.whatsappPhonenumberId ||
            '',
          whatsappToken:
            selectedAssistant.config.whatsAppDetails?.whatsappToken || '',
          whatsappVerificationToken:
            selectedAssistant.config.whatsAppDetails
              ?.whatsappVerificationToken || '',
        },
        tools: selectedAssistant.config.tools || [],
        vad: {
          turnEndpointDelay: stripSecFromValue(
            selectedAssistant.config.vad?.turnEndpointDelay || ''
          ),
          minimumTurnDuration: stripSecFromValue(
            selectedAssistant.config.vad?.minimumTurnDuration || ''
          ),
          minimumInterruptionDuration: stripSecFromValue(
            selectedAssistant.config.vad?.minimumInterruptionDuration || ''
          ),
          frameActivationThreshold:
            selectedAssistant.config.vad?.frameActivationThreshold || 0,
        },
      });
    }
  }, [selectedAssistant, selectedAssistantId]);

  // Add handler for warm transfer toggle
  const handleWarmTransferChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setUpdateAssistantConfigValues(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        warmTransfer: event.target.checked,
      };
    });
  };

  const configOptions = [
    {
      label: 'Model',
      value: updateAssistantConfigValue?.llm || 'OpenAI/gpt-4o',
      options: configValuesData?.llm?.map(item => ({
        value: item,
        label: item,
      })),
    },
    {
      label: 'Select Agent Timezone',
      value: updateAssistantConfigValue?.timeZone || '',
      options:
        timeZones?.map((item: string) => ({
          value: item,
          label: item,
        })) || [],
    },
    {
      label: 'Speech-to-Text Provider',
      value: updateAssistantConfigValue?.stt || 'Deepgram',
      options: configValuesData?.stt?.map(item => ({
        value: item,
        label: item,
      })),
    },
    {
      label: 'Text-to-Speech Provider',
      value: updateAssistantConfigValue?.tts || 'Deepgram',
      options: configValuesData?.tts?.map(item => ({
        value: item,
        label: item,
      })),
    },
  ];

  const commonSelectStyles = {
    '& .MuiSelect-select': {
      fontSize: '14px',
      fontWeight: 400,
      background: '#F8F9FC',
      display: 'flex',
      padding: '8px 12px',
      alignItems: 'center',
      borderRadius: '6px',
      border: '1px solid #DCE0E7',
      lineHeight: 'normal',
      letterSpacing: '-0.14px',
      color: theme.palette.lightGray.text,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  };

  const {
    mutateAsync: updateAssistantConfig,
    isPending: isUpdateAssistantConfigPending,
  } = useMutation({
    mutationFn: (values: IUpdateAssistantConfigValues) =>
      updateAssistantConfigValues(values),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] });
      showAlert('Agent configuration updated', 'success');
      onClose?.();
      const linkedVoiceAgentId =
        selectedAssistant?.config?.whatsappConfig?.linkedVoiceAgentId;
      if (
        selectedAssistant?.agentChannel === 'whatsapp' &&
        linkedVoiceAgentId
      ) {
        connectWhatsappVoiceLinkApi({
          voiceAgentId: linkedVoiceAgentId,
          whatsappAgentId: selectedAssistant._id,
        }).catch(() => {
          // silent fail — config save succeeded, re-sync best-effort
        });
      }
    },
    onError: (error: any) => {
      showAlert(
        error.response?.data?.message || 'Failed to update agent configuration',
        'error'
      );
    },
  });

  // Filter out already selected tools for the dropdown
  const selectedToolIds =
    updateAssistantConfigValue?.tools?.map(tool => tool.toolId) || [];
  const availableToolsForSelection = availableTools.filter(
    (tool: UltravoxToolData) => !selectedToolIds.includes(tool.toolId || '')
  );

  // Handle tool selection from dropdown
  const handleToolSelect = (toolId: string) => {
    if (toolId) {
      const selectedTool = availableTools.find(
        (tool: UltravoxToolData) => tool.toolId === toolId
      );
      if (selectedTool && !selectedToolIds.includes(toolId)) {
        setUpdateAssistantConfigValues(prev => {
          if (!prev) return prev;
          return {
            ...prev,
            tools: [
              ...(prev.tools || []),
              {
                toolName: selectedTool.name,
                toolId: selectedTool.toolId || '',
              },
            ],
          };
        });
      }
    }
  };

  // Handle tool removal
  const handleToolRemove = (toolId: string) => {
    setUpdateAssistantConfigValues(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        tools: prev.tools?.filter(tool => tool.toolId !== toolId) || [],
      };
    });
  };

  const handleSaveConfig = async () => {
    await updateAssistantConfig(updateAssistantConfigValue!);
  };

  const handleSelectChange = (value: string, label: string) => {
    setUpdateAssistantConfigValues(prev => {
      if (!prev) return prev;

      const updatedConfig = { ...prev };
      switch (label) {
        case 'Model':
          updatedConfig.llm = value;
          break;
        case 'Speech-to-Text Provider':
          updatedConfig.stt = value;
          break;
        case 'Text-to-Speech Provider':
          updatedConfig.tts = value;
          break;
        case 'Select Agent Timezone':
          updatedConfig.timeZone = value;
          break;
        case 'Select Preferred Voice':
          updatedConfig.voice = value;
          break;
        case 'Select Agent Language':
          updatedConfig.language = value;
          break;
      }
      return updatedConfig;
    });
  };

  // Handler for slider changes (updates both local state and API state)
  const handleSliderChange = (name: string, value: number) => {
    // Update local state for slider display
    setUpdateAssistantConfigValues(prev => {
      if (!prev) return prev;

      return {
        ...prev,
        [name]: value, // Add the slider value to the config
      };
    });
    // Also update the state that will be sent to the API
    setUpdateAssistantConfigValues(prev => {
      if (!prev) return prev;

      return {
        ...prev,
        [name]: value, // Add the slider value to the config
      };
    });
  };

  // ElevenLabs sliders config
  const sliderOptions = [
    {
      id: 'latency',
      label: 'Optimize streaming latency',
      value: updateAssistantConfigValue?.latency || 0.5,
      min: 'Low',
      max: 'High',
      minValue: 0,
      maxValue: 1,
      description:
        'Configure latency optimizations for the speech generation. Latency can be optimized at the cost of quality.',
    },
    {
      id: 'stability',
      label: 'Stability',
      value: updateAssistantConfigValue?.stability || 0.5,
      min: 'More variable',
      max: 'More stable',
      minValue: 0,
      maxValue: 1,
      description:
        'Higher values will make speech more consistent, but it can also make it sound monotone. Lower values will make speech sound more expressive, but may lead to instabilities.',
    },
    {
      id: 'speed',
      label: 'Speed',
      value: updateAssistantConfigValue?.speed || 1.0,
      min: 'Slower',
      max: 'Faster',
      minValue: 0,
      maxValue: 1.2,
      description:
        'Controls the speed of the generated speech. Values below 1.0 will slow down the speech, while values above 1.0 will speed it up. Extreme values may affect the quality of the generated speech.',
    },
    {
      id: 'similarity',
      label: 'Similarity',
      value: updateAssistantConfigValue?.similarity || 0.75,
      min: 'Less similar',
      max: 'More similar',
      minValue: 0,
      maxValue: 1,
      description:
        'Higher values will boost the overall clarity and consistency of the voice. Very high values may lead to artifacts. Adjusting this value to find the right balance is recommended.',
    },
  ];

  const handleUserTimeoutChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = event.target.value;

    if (inputValue === '') {
      setUpdateAssistantConfigValues(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          userTimeout: undefined,
        };
      });
      return;
    }

    const value = parseFloat(inputValue);
    if (!isNaN(value) && value >= 0) {
      setUpdateAssistantConfigValues(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          userTimeout: value,
        };
      });
    }
  };

  // Update handlers for VAD fields to work with nested structure
  const handleVadFieldChange = (
    field: string,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = event.target.value;

    if (inputValue === '') {
      setUpdateAssistantConfigValues(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          vad: {
            ...prev.vad,
            [field]: undefined,
          },
        };
      });
      return;
    }

    const numValue = parseFloat(inputValue);
    if (!isNaN(numValue) && numValue >= 0) {
      setUpdateAssistantConfigValues(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          vad: {
            ...prev.vad,
            [field]: numValue,
          },
        };
      });
    }
  };

  const handleFrameActivationThresholdChange = (
    _event: Event,
    value: number | number[]
  ) => {
    const numValue = Array.isArray(value) ? value[0] : value;
    setUpdateAssistantConfigValues(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        vad: {
          ...prev.vad,
          frameActivationThreshold: numValue,
        },
      };
    });
  };

  // WhatsApp config handlers - similar to voice config handlers
  const handleWhatsappModelChange = (value: string) => {
    setUpdateAssistantConfigValues(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        whatsappConfig: {
          ...prev.whatsappConfig!,
          model: value,
        },
      };
    });
  };

  const handleWhatsappHistoryResponsesChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const inputValue = event.target.value;

    if (inputValue === '') {
      setUpdateAssistantConfigValues(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          whatsappConfig: {
            ...prev.whatsappConfig!,
            historyResponse: undefined,
          },
        };
      });
      return;
    }

    const value = parseInt(inputValue, 10);
    if (!isNaN(value) && value >= 0) {
      setUpdateAssistantConfigValues(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          whatsappConfig: {
            ...prev.whatsappConfig!,
            historyResponse: value,
          },
        };
      });
    }
  };

  const handleWhatsappDebugModeChange = (value: boolean) => {
    setUpdateAssistantConfigValues(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        whatsappConfig: {
          ...prev.whatsappConfig!,
          debugMode: value,
        },
      };
    });
  };

  const handleWhatsappPhoneNumberIdChange = (value: string) => {
    setUpdateAssistantConfigValues(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        whatsappConfig: {
          ...prev.whatsappConfig!,
          whatsappPhonenumberId: value,
        },
      };
    });
  };

  const handleWhatsappTokenChange = (value: string) => {
    setUpdateAssistantConfigValues(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        whatsappConfig: {
          ...prev.whatsappConfig!,
          whatsappToken: value,
        },
      };
    });
  };

  const handleWhatsappVerifyTokenChange = (value: string) => {
    setUpdateAssistantConfigValues(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        whatsappConfig: {
          ...prev.whatsappConfig!,
          whatsappVerificationToken: value,
        },
      };
    });
  };

  return (
    <>
      <Box sx={{ width: '100%', bgcolor: '#fff', height: '100%' }}>
        {isDrawer && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
              px: 2,
              py: 1,
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton onClick={onClose}>
                <XIcon size={20} />
              </IconButton>
              <Typography
                variant="h6"
                sx={{ fontSize: '18px', fontWeight: 600 }}
              >
                Agent Configuration
              </Typography>
            </Box>
            <Button
              variant="contained"
              onClick={() => {
                void handleSaveConfig();
              }}
              disabled={isUpdateAssistantConfigPending}
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: '6px',
                boxShadow: 'none',
              }}
            >
              {isUpdateAssistantConfigPending ? 'Updating...' : 'Save Config'}
            </Button>
          </Box>
        )}

        {selectedAssistant?.agentChannel === 'whatsapp' ? (
          <AgentWhatsappConfig
            isDrawer={isDrawer}
            whatsappConfig={{
              model:
                updateAssistantConfigValue?.whatsappConfig?.model ||
                'OpenAI/gpt-4o',
              historyResponse:
                updateAssistantConfigValue?.whatsappConfig?.historyResponse ||
                undefined,
              debugMode:
                updateAssistantConfigValue?.whatsappConfig?.debugMode || false,
              whatsappPhonenumberId:
                updateAssistantConfigValue?.whatsappConfig
                  ?.whatsappPhonenumberId || '',
              whatsappToken:
                updateAssistantConfigValue?.whatsappConfig?.whatsappToken || '',
              whatsappVerificationToken:
                updateAssistantConfigValue?.whatsappConfig
                  ?.whatsappVerificationToken || '',
            }}
            onModelChange={handleWhatsappModelChange}
            onHistoryResponsesChange={handleWhatsappHistoryResponsesChange}
            onDebugModeChange={handleWhatsappDebugModeChange}
            onWhatsappPhoneNumberIdChange={handleWhatsappPhoneNumberIdChange}
            onWhatsappTokenChange={handleWhatsappTokenChange}
            onWhatsappVerifyTokenChange={handleWhatsappVerifyTokenChange}
            modelOptions={configValuesData?.llm?.map(item => ({
              value: item,
              label: item,
            }))}
          />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              mt: 1,
              px: isDrawer ? 2 : 0,
            }}
          >
            {/* Model and Timezone selectors */}
            {configOptions.slice(0, 2).map((config: any, index: number) => (
              <FormControl fullWidth key={index} size="small">
                <FormLabel sx={commonStyles.formLabel}>
                  {config.label}
                </FormLabel>
                <Select
                  value={config.value || ''}
                  defaultValue={config.value || ''}
                  sx={commonSelectStyles}
                  inputProps={{ 'aria-label': 'Without label' }}
                  onChange={e =>
                    handleSelectChange(e.target.value, config.label)
                  }
                >
                  {Array.isArray(config.options)
                    ? config.options.map((option: any) => (
                        <MenuItem key={option.value} value={option.label}>
                          {option.label}
                        </MenuItem>
                      ))
                    : null}
                </Select>
              </FormControl>
            ))}

            {/* STT and TTS providers */}
            {configOptions.slice(2).map((config: any, index: number) => (
              <FormControl fullWidth key={index} size="small">
                <FormLabel sx={commonStyles.formLabel}>
                  {config.label}
                </FormLabel>
                <Select
                  value={config.value || ''}
                  defaultValue={config.value || ''}
                  sx={commonSelectStyles}
                  inputProps={{ 'aria-label': 'Without label' }}
                  onChange={e =>
                    handleSelectChange(e.target.value, config.label)
                  }
                >
                  {Array.isArray(config.options)
                    ? config.options.map((option: any) => (
                        <MenuItem key={option.value} value={option.label}>
                          {option.label}
                        </MenuItem>
                      ))
                    : null}
                </Select>
              </FormControl>
            ))}

            <FormControl>
              <FormLabel sx={commonStyles.formLabel}>Agent Language</FormLabel>
              <Stack direction="row" spacing={4}>
                <Tooltip
                  title={
                    'You can change the language of the assistant by clicking on the button below(Change Voice) but you have to change the voice also accordingly'
                  }
                >
                  <TextField
                    name="language"
                    required
                    fullWidth
                    variant="outlined"
                    disabled
                    value={selectedAssistantConfig?.language?.name || ''}
                    placeholder="No Language Selected"
                    sx={{
                      ...commonStyles.textField,
                      '& .MuiInputBase-input.Mui-disabled': {
                        color: '#2B2D3F',
                        WebkitTextFillColor: '#2B2D3F',
                      },
                    }}
                  />
                </Tooltip>
              </Stack>
            </FormControl>

            <FormControl>
              <FormLabel sx={commonStyles.formLabel}>
                Assistance Voice
              </FormLabel>
              <Stack direction="row" spacing={4}>
                <TextField
                  name="voice"
                  required
                  fullWidth
                  variant="outlined"
                  disabled
                  value={
                    selectedAssistantConfig?.voice?.name ||
                    selectedAssistantConfig?.voice?.voiceName ||
                    ''
                  }
                  placeholder="No Voice Selected"
                  sx={{
                    ...commonStyles.textField,
                    '& .MuiInputBase-input.Mui-disabled': {
                      color: '#2B2D3F',
                      WebkitTextFillColor: '#2B2D3F',
                    },
                  }}
                />

                <PermissionGate
                  sectionKey="voice_library"
                  tooltipMessage="You need permission to access Voice Library. Contact your organization owner."
                >
                  <Button
                    variant="text"
                    sx={{
                      width: '50%',
                      textTransform: 'none',
                      color: 'primary.main',
                    }}
                    onClick={() =>
                      void navigate(
                        `${routes.voiceLibrary}?capability=monolingual`
                      )
                    }
                  >
                    {selectedAssistantConfig?.voice?.name
                      ? 'Change Voice'
                      : 'Select Voice '}
                  </Button>
                </PermissionGate>
              </Stack>
            </FormControl>

            <FormControl>
              <Tooltip title="the system will wait for the specified duration of silence before deciding that you've finished speaking">
                <FormLabel sx={commonStyles.formLabel}>
                  User Idle Timeout (sec)
                </FormLabel>
              </Tooltip>
              <TextField
                name="userTimeout"
                type="number"
                fullWidth
                variant="outlined"
                value={updateAssistantConfigValue?.userTimeout ?? ''}
                onChange={handleUserTimeoutChange}
                sx={commonStyles.textField}
              />
            </FormControl>

            {/* Warm Transfer Toggle */}
            <Box
              sx={{
                mt: 1,
                mb: 1,
                p: 1,
                border: '1px solid #DCE0E7',
                borderRadius: '8px',
                bgcolor: '#FFFFFF',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                sx={{
                  fontSize: '14px',
                  fontWeight: 500,
                  color: '#2B2D3F',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                Warm Transfer
              </Typography>
              <Switch
                checked={updateAssistantConfigValue?.warmTransfer || false}
                onChange={handleWarmTransferChange}
                color="primary"
                size="small"
                sx={{
                  width: 36,
                  height: 20,
                  padding: 0,
                  '& .MuiSwitch-switchBase': {
                    padding: 0,
                    margin: '2px',
                    transitionDuration: '300ms',
                    '&.Mui-checked': {
                      transform: 'translateX(16px)',
                      color: '#fff',
                      '& + .MuiSwitch-track': {
                        backgroundColor: '#6366F1',
                        opacity: 1,
                        border: 0,
                      },
                    },
                    '&.Mui-focusVisible .MuiSwitch-thumb': {
                      color: '#6366F1',
                      border: '6px solid #fff',
                    },
                  },
                  '& .MuiSwitch-thumb': {
                    boxSizing: 'border-box',
                    width: 16,
                    height: 16,
                  },
                  '& .MuiSwitch-track': {
                    borderRadius: 26 / 2,
                    backgroundColor: '#E9E9EA',
                    opacity: 1,
                  },
                }}
              />
            </Box>

            {/* Voice Activity Detection Section */}
            <Box
              sx={{
                mt: 1,
                mb: 1,
                p: 2,
                border: '1px solid #DCE0E7',
                borderRadius: '8px',
                bgcolor: '#FFFFFF',
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  fontSize: '14px',
                  fontWeight: 600,
                  color: theme.palette.text.primary,
                  mb: 2,
                }}
              >
                Voice Activity Detection
              </Typography>

              {/* Turn Endpoint Delay */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 400,
                      color: 'lightGray.text',
                    }}
                  >
                    Turn Endpoint Delay (sec)
                  </Typography>
                  <Tooltip title="Time to wait before considering a turn ended">
                    <IconButton size="small" sx={{ padding: 0 }}>
                      <InfoIcon size={12} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  name="turnEndpointDelay"
                  type="number"
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    inputProps: {
                      min: 0,
                      step: 0.1,
                    },
                  }}
                  value={
                    updateAssistantConfigValue?.vad?.turnEndpointDelay ?? ''
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleVadFieldChange('turnEndpointDelay', e)
                  }
                  sx={commonStyles.textField}
                />
              </FormControl>

              {/* Minimum Turn Duration */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 400,
                      color: 'lightGray.text',
                    }}
                  >
                    Minimum Turn Duration (sec)
                  </Typography>
                  <Tooltip title="Minimum duration for a valid turn">
                    <IconButton size="small" sx={{ padding: 0 }}>
                      <InfoIcon size={12} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  name="minimumTurnDuration"
                  type="number"
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    inputProps: {
                      min: 0,
                      step: 0.1,
                    },
                  }}
                  value={
                    updateAssistantConfigValue?.vad?.minimumTurnDuration ?? ''
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleVadFieldChange('minimumTurnDuration', e)
                  }
                  sx={commonStyles.textField}
                />
              </FormControl>

              {/* Minimum Interruption Duration */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 400,
                      color: 'lightGray.text',
                    }}
                  >
                    Minimum Interruption Duration (sec)
                  </Typography>
                  <Tooltip title="Minimum time required to interrupt the agent">
                    <IconButton size="small" sx={{ padding: 0 }}>
                      <InfoIcon size={12} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <TextField
                  name="minimumInterruptionDuration"
                  type="number"
                  fullWidth
                  variant="outlined"
                  InputProps={{
                    inputProps: {
                      min: 0,
                      step: 0.1,
                    },
                  }}
                  value={
                    updateAssistantConfigValue?.vad
                      ?.minimumInterruptionDuration ?? ''
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    handleVadFieldChange('minimumInterruptionDuration', e)
                  }
                  sx={commonStyles.textField}
                />
              </FormControl>

              {/* Frame Activation Threshold */}
              <FormControl fullWidth>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    mb: 1,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 400,
                      color: 'lightGray.text',
                    }}
                  >
                    Frame Activation Threshold
                  </Typography>
                  <Tooltip title="Sensitivity threshold for voice activation detection">
                    <IconButton size="small" sx={{ padding: 0 }}>
                      <InfoIcon size={12} />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    mt: 1,
                  }}
                >
                  <Slider
                    value={
                      updateAssistantConfigValue?.vad
                        ?.frameActivationThreshold || 0.0
                    }
                    onChange={handleFrameActivationThresholdChange}
                    min={0}
                    max={1}
                    step={0.01}
                    valueLabelDisplay="auto"
                    valueLabelFormat={value => value.toFixed(2)}
                    sx={{
                      flex: 1,
                      color: 'primary.main',
                      '& .MuiSlider-thumb': {
                        width: 16,
                        height: 16,
                        backgroundColor: '#fff',
                        border: '2px solid currentColor',
                      },
                      '& .MuiSlider-track': {
                        height: 2,
                      },
                      '& .MuiSlider-rail': {
                        height: 2,
                        opacity: 0.5,
                        backgroundColor: '#E5E7EB',
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontWeight: 500,
                      color: theme.palette.text.primary,
                      minWidth: '32px',
                      textAlign: 'right',
                    }}
                  >
                    {(
                      updateAssistantConfigValue?.vad
                        ?.frameActivationThreshold || 0.0
                    ).toFixed(2)}
                  </Typography>
                </Box>
              </FormControl>
            </Box>

            {/* Tools Selection Section */}
            <Box
              sx={{
                mt: 1,
                mb: 1,
                p: 2,
                border: '1px solid #DCE0E7',
                borderRadius: '8px',
                bgcolor: '#FFFFFF',
              }}
            >
              <FormLabel sx={commonStyles.formLabel}>Available Tools</FormLabel>

              {/* Tool Selection Area */}
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}
              >
                {/* Selected Tools Display */}
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    width: '100%',
                    minHeight: '60px',
                    bgcolor: 'white',
                    p: 2,
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 0, 0, 0.23)',
                  }}
                >
                  {updateAssistantConfigValue?.tools &&
                  updateAssistantConfigValue.tools.length > 0 ? (
                    updateAssistantConfigValue.tools.map((tool, index) => {
                      const toolData = availableTools.find(
                        (t: UltravoxToolData) => t.toolId === tool.toolId
                      );
                      return (
                        <Chip
                          key={index}
                          label={toolData?.name || tool.toolName || tool.toolId}
                          onDelete={() => handleToolRemove(tool.toolId)}
                          sx={{
                            height: '32px',
                            fontSize: '14px',
                            '& .MuiChip-label': {
                              px: 1.5,
                            },
                          }}
                        />
                      );
                    })
                  ) : (
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'text.secondary',
                        fontStyle: 'italic',
                        display: 'flex',
                        alignItems: 'center',
                        minHeight: '32px',
                        width: '100%',
                        justifyContent: 'center',
                      }}
                    >
                      No tools selected
                    </Typography>
                  )}
                </Box>

                {/* Tool Selection Dropdown */}
                <Select
                  variant="outlined"
                  size="small"
                  onChange={e => handleToolSelect(e.target.value)}
                  value=""
                  displayEmpty
                  renderValue={() => 'Select tool'}
                  fullWidth
                  sx={{
                    height: '48px',
                    borderRadius: '6px',
                    textTransform: 'none',
                    color: 'text.secondary',
                    borderColor: 'lightGray.main',
                    fontSize: '14px',
                    bgcolor: '#F8F9FC',
                    '& .MuiSelect-select': {
                      py: 1.5,
                    },
                  }}
                >
                  {availableToolsForSelection.length === 0 ? (
                    <MenuItem disabled>
                      {isToolsLoading
                        ? 'Loading tools...'
                        : 'No tools available'}
                    </MenuItem>
                  ) : (
                    availableToolsForSelection.map((tool: UltravoxToolData) => (
                      <MenuItem key={tool.toolId} value={tool.toolId}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                          }}
                        >
                          <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            {tool.name}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary' }}
                          >
                            {tool.definition.description}
                          </Typography>
                          <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', fontSize: '10px' }}
                          >
                            {tool.ownership === 'public'
                              ? 'Built-in'
                              : 'Custom'}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
              </Box>
            </Box>

            {/* Display ElevenLabs sliders only when TTS provider is ElevenLabs */}
            {updateAssistantConfigValue?.tts === 'Elevenlabs' && (
              <Box sx={{ mt: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: '14px',
                    color: theme.palette.text.secondary,
                  }}
                >
                  Only applicable for ElevenLabs TTS
                </Typography>
                {sliderOptions.map(option => (
                  <FormControl fullWidth key={option.id} size="small">
                    <Tooltip title={option.description}>
                      <FormLabel
                        sx={{
                          fontSize: '12px',
                          fontWeight: 500,
                          mt: '10px',
                        }}
                      >
                        {option.label}
                      </FormLabel>
                    </Tooltip>
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: '10px',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '12px',
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {option.min}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '12px',
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {option.max}
                      </Typography>
                    </Box>
                    <Slider
                      value={option.value}
                      onChange={(_, value) =>
                        handleSliderChange(option.id, value as number)
                      }
                      min={option.minValue}
                      max={option.maxValue}
                      step={0.01}
                      valueLabelDisplay="auto"
                      getAriaValueText={value => value.toFixed(2)}
                      valueLabelFormat={value => value.toFixed(2)}
                      sx={{
                        color: 'primary.main',
                        '& .MuiSlider-thumb': {
                          width: 16,
                          height: 16,
                          backgroundColor: '#fff',
                          border: '2px solid currentColor',
                        },
                        '& .MuiSlider-track': {
                          height: 2,
                        },
                        '& .MuiSlider-rail': {
                          height: 2,
                          opacity: 0.5,
                          backgroundColor: '#E5E7EB',
                        },
                      }}
                    />
                  </FormControl>
                ))}
              </Box>
            )}
          </Box>
        )}
      </Box>
      {/* Only render MessagePopup when not in drawer mode */}
      {!isDrawer && <MessagePopup alert={alert} setAlert={setAlert} />}
    </>
  );
};

export default AgentConfiguration;
